import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Truck, LogOut, Calendar } from "lucide-react";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);
const monthAgo = () => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10); };

export default function AccountingDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [start, setStart] = useState(monthAgo());
  const [end, setEnd] = useState(today());
  const [data, setData] = useState({ accounts: [], grand_total: {} });

  const load = useCallback(async () => {
    try {
      const r = await api.get("/accounting/summary", { params: { start, end } });
      setData(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
  }, [start, end]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-border px-6 py-4 flex items-center justify-between bg-card">
        <div className="flex items-center gap-4">
          <div className="w-9 h-9 bg-brand-green flex items-center justify-center"><Truck className="w-5 h-5 text-black" strokeWidth={2.5}/></div>
          <div>
            <div className="font-display text-lg uppercase tracking-tight">COUNTRY COURIER // ACCOUNTING</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{user.name}</div>
          </div>
        </div>
        <Button variant="outline" onClick={()=>{logout();navigate("/");}} className="font-mono uppercase text-xs" data-testid="logout-btn"><LogOut className="w-3 h-3 mr-2"/>Sign Out</Button>
      </header>

      <div className="p-6 space-y-6">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="space-y-1"><Label className="font-mono text-[10px] uppercase tracking-widest">Start</Label>
            <Input type="date" value={start} onChange={(e)=>setStart(e.target.value)} className="h-11 bg-white border-2 font-mono" data-testid="date-start"/></div>
          <div className="space-y-1"><Label className="font-mono text-[10px] uppercase tracking-widest">End</Label>
            <Input type="date" value={end} onChange={(e)=>setEnd(e.target.value)} className="h-11 bg-white border-2 font-mono" data-testid="date-end"/></div>
          <Button onClick={load} className="h-11 bg-brand-green text-black hover:bg-brand-green/90 font-mono uppercase font-bold" data-testid="refresh-btn"><Calendar className="w-4 h-4 mr-2"/>Refresh</Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border">
          {[["Bookings",data.grand_total?.count||0],["Subtotal",`$${(data.grand_total?.subtotal||0).toFixed(2)}`],["GST",`$${(data.grand_total?.gst||0).toFixed(2)}`],["Grand Total",`$${(data.grand_total?.total||0).toFixed(2)}`]].map(([l,v])=>(
            <div key={l} className="bg-card p-4"><div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{l}</div><div className="text-3xl font-display text-brand-green">{v}</div></div>
          ))}
        </div>

        <div className="border-2 border-border overflow-x-auto" data-testid="accounting-table">
          <table className="w-full font-mono text-xs">
            <thead className="bg-muted/50">
              <tr className="text-left uppercase tracking-widest text-[10px]">
                <th className="p-3">Account</th><th className="p-3">Type</th><th className="p-3 text-right">Bookings</th>
                <th className="p-3 text-right">Subtotal</th><th className="p-3 text-right">GST</th><th className="p-3 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {data.accounts.map(a => (
                <tr key={a.account_id} className="border-t border-border" data-testid={`acct-row-${a.account_code}`}>
                  <td className="p-3"><div className="text-brand-green font-bold">{a.account_code}</div><div className="text-muted-foreground">{a.account_name}</div></td>
                  <td className="p-3 uppercase">{a.account_type}</td>
                  <td className="p-3 text-right">{a.count}</td>
                  <td className="p-3 text-right">${a.subtotal.toFixed(2)}</td>
                  <td className="p-3 text-right">${a.gst.toFixed(2)}</td>
                  <td className="p-3 text-right text-brand-green font-bold">${a.total.toFixed(2)}</td>
                </tr>
              ))}
              {data.accounts.length === 0 && <tr><td colSpan="6" className="p-8 text-center uppercase tracking-widest text-muted-foreground">No deliveries in range</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
